import { useState, useEffect } from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import { API } from 'aws-amplify';
import { auth } from '../firebase';

const useImpersonation = () => {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationDetails, setImpersonationDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if currently impersonating on mount and when auth state changes
  useEffect(() => {
    const checkImpersonation = async () => {
      const { currentUser } = auth;
      if (currentUser) {
        const tokenResult = await currentUser.getIdTokenResult();
        const { claims } = tokenResult;

        if (claims.isImpersonation === true) {
          setIsImpersonating(true);
          setImpersonationDetails({
            adminUid: claims.impersonatedBy,
            adminName: claims.impersonatedByName,
            targetUid: claims.user_id,
            targetName: claims.name,
            startTime: claims.impersonationStartTime,
          });
        } else {
          setIsImpersonating(false);
          setImpersonationDetails(null);
        }
      }
    };

    checkImpersonation();

    // Listen for auth state changes
    const unsubscribe = auth.onAuthStateChanged(checkImpersonation);

    return () => unsubscribe();
  }, []);

  const startImpersonation = async (targetUid) => {
    setLoading(true);
    setError(null);

    try {
      const { currentUser } = auth;
      if (!currentUser) {
        throw new Error('Not authenticated');
      }

      const idToken = await currentUser.getIdToken();

      // Call backend to get impersonation token
      const response = await API.post('lw-dash', `/users/${targetUid}/impersonate`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      const { customToken, targetUser, impersonationDetails: details } = response;

      // Sign in with the custom token
      await signInWithCustomToken(auth, customToken);

      setIsImpersonating(true);
      setImpersonationDetails({
        ...details,
        targetEmail: targetUser.email,
        targetDisplayName: targetUser.displayName,
      });

      setLoading(false);

      // Redirect to homepage after successful impersonation
      window.location.href = '/';

      return { success: true, targetUser };
    } catch (err) {
      console.error('Failed to start impersonation:', err);
      setError(err.message || 'Failed to start impersonation');
      setLoading(false);
      return { success: false, error: err.message };
    }
  };

  const stopImpersonation = async () => {
    setLoading(true);
    setError(null);

    try {
      const { currentUser } = auth;
      if (!currentUser) {
        throw new Error('Not authenticated');
      }

      const idToken = await currentUser.getIdToken();

      // Call backend to log impersonation end
      await API.post('lw-dash', '/users/stop-impersonation', {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      // Sign out to end the impersonation session
      await auth.signOut();

      // User will need to sign in again with their own credentials
      setIsImpersonating(false);
      setImpersonationDetails(null);
      setLoading(false);

      // Reload the page to clear all state
      window.location.href = '/';

      return { success: true };
    } catch (err) {
      console.error('Failed to stop impersonation:', err);
      setError(err.message || 'Failed to stop impersonation');
      setLoading(false);
      return { success: false, error: err.message };
    }
  };

  return {
    isImpersonating,
    impersonationDetails,
    loading,
    error,
    startImpersonation,
    stopImpersonation,
  };
};

export default useImpersonation;
